import React, { useCallback, useEffect, useState } from 'react';
import { fetchPartnerDashboard } from '../../lib/api';
import StatsBlock from './StatsBlock';
import SBTCertificate from './SBTCertificate';
import RefillsLog from './RefillsLog';
import UsageChart from './UsageChart';
import BulkOrderForm from './BulkOrderForm';

const PartnerPortal = ({ partnerId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    if (!partnerId) return;
    setLoading(true);
    setError('');
    fetchPartnerDashboard(partnerId)
      .then(setData)
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Could not load partner portal.');
      })
      .finally(() => setLoading(false));
  }, [partnerId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!partnerId) return null;

  if (loading) {
    return (
      <div data-testid="partner-loading" className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-40 bg-[#C1D7E2]/40 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="partner-error"
        className="border border-[#C1D7E2] bg-[#F4F8F9] p-10 text-center"
      >
        <p className="font-serif italic text-2xl text-[#7C9CB1]">{error}</p>
        <p className="mt-3 font-mono text-[11px] tracking-[0.2em] uppercase text-[#90B0BF]">
          Check the partner ID and try again.
        </p>
      </div>
    );
  }

  const { partner, sbt, totals, by_product: byProduct, refills, recent_usage, bulk_orders } = data;

  return (
    <div data-testid="partner-portal-view" className="space-y-16">
      <header
        data-testid="partner-identity"
        className="flex flex-col md:flex-row items-start md:items-center gap-6 pb-8 border-b border-[#C1D7E2]"
      >
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
            Partner Portal · {partner.property_type}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#7C9CB1] font-light leading-tight">
            {partner.name}
          </h2>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 font-mono text-[11px] text-[#90B0BF]">
            <span>{partner.location || '—'}</span>
            <span>{partner.rooms} rooms</span>
            <span className="uppercase tracking-[0.25em]">Tier · {partner.tier}</span>
            <span className="uppercase tracking-[0.25em]">Status · {partner.status}</span>
          </div>
        </div>
        <div
          data-testid="partner-api-key"
          className="font-mono text-xs text-[#7C9CB1] border border-[#C1D7E2] px-4 py-3 break-all max-w-xs"
        >
          <p className="text-[#90B0BF] tracking-[0.25em] uppercase text-[10px] mb-1">
            API key
          </p>
          {partner.api_key}
        </div>
      </header>

      <StatsBlock partner={partner} totals={totals} />

      <section data-testid="partner-section-sbt">
        <h3 className="font-serif text-3xl text-[#7C9CB1] font-light mb-6">
          Verified Partner Certificate
        </h3>
        <SBTCertificate sbt={sbt} partner={partner} />
      </section>

      <section data-testid="partner-section-refills">
        <h3 className="font-serif text-3xl text-[#7C9CB1] font-light mb-6">
          Refill Handshake
        </h3>
        <RefillsLog
          partnerId={partnerId}
          initialRefills={refills || []}
          onRefilled={load}
        />
      </section>

      <section data-testid="partner-section-usage">
        <h3 className="font-serif text-3xl text-[#7C9CB1] font-light mb-6">
          Pay-Per-Use Monitoring
        </h3>
        <UsageChart
          partnerId={partnerId}
          byProduct={byProduct || []}
          recentUsage={recent_usage || []}
          onLogged={load}
        />
      </section>

      <section data-testid="partner-section-bulk">
        <h3 className="font-serif text-3xl text-[#7C9CB1] font-light mb-6">
          Bulk Orders
        </h3>
        <BulkOrderForm
          partnerId={partnerId}
          sevaBalance={partner.seva_balance}
          bulkOrders={bulk_orders || []}
          onOrdered={load}
        />
      </section>
    </div>
  );
};

export default PartnerPortal;
